import { sql, type SQL } from "drizzle-orm";
import { getTableName } from "drizzle-orm";
import { pgPolicy, type AnyPgColumn, type PgTable } from "drizzle-orm/pg-core";

import { stringifySessionValue } from "./rls-session.js";

export type RlsPolicyCommand = "select" | "insert" | "update" | "delete";

export type RlsPolicyCommands = RlsPolicyCommand[] | "all";

export type SessionAllowRules = {
  all?: readonly (string | boolean | number)[];
  select?: readonly (string | boolean | number)[];
  insert?: readonly (string | boolean | number)[];
  update?: readonly (string | boolean | number)[];
  delete?: readonly (string | boolean | number)[];
};

export type RlsHelperOptions = {
  commands?: RlsPolicyCommands;
};

type Constraint = {
  commands: RlsPolicyCommand[];
  expr: SQL;
};

const ALL_COMMANDS: RlsPolicyCommand[] = ["select", "insert", "update", "delete"];

function normalizeCommands(commands?: RlsPolicyCommands): RlsPolicyCommand[] {
  if (!commands || commands === "all") return [...ALL_COMMANDS];
  return commands;
}

/** Inline SQL string literals so drizzle-kit migrations do not emit `$1` bind placeholders. */
function escapeSqlStringLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

function sqlStringLiteral(value: string): SQL {
  return sql.raw(`'${escapeSqlStringLiteral(value)}'`);
}

function andExprs(exprs: SQL[]): SQL {
  if (exprs.length === 0) return sql`true`;
  if (exprs.length === 1) return exprs[0]!;
  return sql.join(
    exprs.map((e) => sql`(${e})`),
    sql` AND `,
  );
}

function orExprs(exprs: SQL[]): SQL {
  if (exprs.length === 0) return sql`false`;
  if (exprs.length === 1) return exprs[0]!;
  return sql.join(
    exprs.map((e) => sql`(${e})`),
    sql` OR `,
  );
}

function combinedExprForCommand(constraints: Constraint[], command: RlsPolicyCommand): SQL | undefined {
  const exprs = constraints.filter((c) => c.commands.includes(command)).map((c) => c.expr);
  if (exprs.length === 0) return undefined;
  return andExprs(exprs);
}

function inferTableNameFromColumns(t: Record<string, AnyPgColumn>): string {
  for (const col of Object.values(t)) {
    if (col && typeof col === "object" && "table" in col) {
      const table = (col as { table?: PgTable }).table;
      if (table) return getTableName(table);
    }
  }
  throw new Error(
    "[tableRls] Could not infer table name from columns. Pass a valid pgTable callback argument (e.g. (t) => tableRls(t)...).",
  );
}

/** @internal Used by ownerRls when only an owner column is available. */
export function inferTableNameFromColumn(column: AnyPgColumn): string {
  const table = (column as { table?: PgTable }).table;
  if (!table) {
    throw new Error(
      "[tableRls] Column has no .table reference. Use ownerRls inside pgTable extraConfig: (t) => ownerRls(t.userId).",
    );
  }
  return getTableName(table);
}

export class TableRlsBuilder {
  private readonly constraints: Constraint[] = [];
  private readPublicSelect = false;

  constructor(private readonly tableName: string) {}

  matchColumn(column: AnyPgColumn, keySql: SQL, options?: RlsHelperOptions): this {
    const expr = sql`${column} = ${keySql}`;
    this.constraints.push({ commands: normalizeCommands(options?.commands), expr });
    return this;
  }

  sessionEq(keySql: SQL, value: string | boolean | number, options?: RlsHelperOptions): this {
    const expr = sql`${keySql} = ${sqlStringLiteral(stringifySessionValue(value))}`;
    this.constraints.push({ commands: normalizeCommands(options?.commands), expr });
    return this;
  }

  sessionIn(keySql: SQL, values: readonly (string | boolean | number)[], options?: RlsHelperOptions): this {
    const literals = values.map((v) => stringifySessionValue(v));
    const expr =
      literals.length === 0
        ? sql`false`
        : sql`${keySql} IN (${sql.join(
            literals.map((v) => sqlStringLiteral(v)),
            sql`, `,
          )})`;
    this.constraints.push({ commands: normalizeCommands(options?.commands), expr });
    return this;
  }

  sessionAllow(keySql: SQL, rules: SessionAllowRules, options?: RlsHelperOptions): this {
    const defaultCommands = normalizeCommands(options?.commands);
    const byCommand: Partial<Record<RlsPolicyCommand, readonly (string | boolean | number)[]>> = rules.all
      ? {
          select: rules.all,
          insert: rules.all,
          update: rules.all,
          delete: rules.all,
        }
      : {
          select: rules.select,
          insert: rules.insert,
          update: rules.update,
          delete: rules.delete,
        };

    for (const command of ALL_COMMANDS) {
      const values = byCommand[command];
      if (!values || values.length === 0) continue;
      const commands =
        rules.all || !(rules.select || rules.insert || rules.update || rules.delete)
          ? defaultCommands
          : ([command] as RlsPolicyCommand[]);
      this.sessionIn(keySql, values, { commands });
    }
    return this;
  }

  sessionGte(keySql: SQL, n: number, options?: RlsHelperOptions): this {
    return this.sessionCompare(keySql, ">=", n, options);
  }

  sessionGt(keySql: SQL, n: number, options?: RlsHelperOptions): this {
    return this.sessionCompare(keySql, ">", n, options);
  }

  sessionLte(keySql: SQL, n: number, options?: RlsHelperOptions): this {
    return this.sessionCompare(keySql, "<=", n, options);
  }

  sessionLt(keySql: SQL, n: number, options?: RlsHelperOptions): this {
    return this.sessionCompare(keySql, "<", n, options);
  }

  private sessionCompare(keySql: SQL, op: ">=" | ">" | "<=" | "<", n: number, options?: RlsHelperOptions): this {
    const bound = sql.raw(String(n));
    const expr = (() => {
      switch (op) {
        case ">=":
          return sql`(${keySql})::numeric >= ${bound}`;
        case ">":
          return sql`(${keySql})::numeric > ${bound}`;
        case "<=":
          return sql`(${keySql})::numeric <= ${bound}`;
        case "<":
          return sql`(${keySql})::numeric < ${bound}`;
      }
    })();
    this.constraints.push({ commands: normalizeCommands(options?.commands), expr });
    return this;
  }

  readPublic(): this {
    this.readPublicSelect = true;
    return this;
  }

  where(fragment: SQL, options?: RlsHelperOptions): this {
    this.constraints.push({ commands: normalizeCommands(options?.commands), expr: fragment });
    return this;
  }

  orGroup(fn: (inner: TableRlsBuilder) => void, options?: RlsHelperOptions): this {
    const inner = new TableRlsBuilder(this.tableName);
    fn(inner);
    const commands = normalizeCommands(options?.commands);
    for (const command of commands) {
      const innerExprs = inner.constraints.filter((c) => c.commands.includes(command)).map((c) => c.expr);
      if (innerExprs.length === 0) continue;
      this.constraints.push({ commands: [command], expr: orExprs(innerExprs) });
    }
    return this;
  }

  build(): ReturnType<typeof pgPolicy>[] {
    const policies: ReturnType<typeof pgPolicy>[] = [];
    const counters: Record<RlsPolicyCommand, number> = {
      select: 0,
      insert: 0,
      update: 0,
      delete: 0,
    };

    for (const command of ALL_COMMANDS) {
      const combined = combinedExprForCommand(this.constraints, command);
      if (command === "select" && this.readPublicSelect) {
        policies.push(
          pgPolicy(this.policyName(command, counters[command]++), {
            for: "select",
            using: sql`true`,
          }),
        );
        continue;
      }
      if (!combined) continue;

      const name = this.policyName(command, counters[command]++);
      if (command === "select") {
        policies.push(pgPolicy(name, { for: "select", using: combined }));
      } else if (command === "insert") {
        policies.push(pgPolicy(name, { for: "insert", withCheck: combined }));
      } else if (command === "update") {
        policies.push(pgPolicy(name, { for: "update", using: combined, withCheck: combined }));
      } else {
        policies.push(pgPolicy(name, { for: "delete", using: combined }));
      }
    }

    return policies;
  }

  private policyName(command: RlsPolicyCommand, index: number): string {
    return `rls_${this.tableName}_${command}_${index}`;
  }
}

/**
 * Fluent PostgreSQL RLS policy builder for `pgTable` extra config.
 *
 * @example
 * ```ts
 * import { tableRls, rlsContextKeys } from "@gencow/core";
 *
 * const ctx = rlsContextKeys<{ userId: string; role: string }>();
 *
 * tableRls(t)
 *   .matchColumn(t.tenantId, ctx("tenantId"))
 *   .matchColumn(t.userId, ctx("userId"))
 *   .sessionAllow(ctx("role"), { select: ["user", "admin"], delete: ["admin"] })
 *   .build();
 * ```
 */
export function tableRls<T extends Record<string, AnyPgColumn>>(t: T): TableRlsBuilder {
  return new TableRlsBuilder(inferTableNameFromColumns(t));
}
